import React from 'react';
import { HeroSection } from '../components/HeroSection';
import { AnimatedSection } from '../components/AnimatedSection';
import { TextReveal, CountUp, Marquee, ShinyText, GradientText } from '../components/Animations';
import { CardGrid } from '../components/CardGrid';
import { SignalRibbon, IntegrationWall } from '../components/Graphics';
import { Shield, Zap, Target, Activity, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlaceholderGraphic } from '../components/PlaceholderGraphic';
export function Home() {
  return <div className="pb-20" data-id="element-354">
      <HeroSection eyebrow="Picus Platform" headline="Autonomous exposure validation for teams that need proof, not more noise" subhead="Picus Platform gives security teams real-time, context-rich validation across controls, exposures, and attack paths. It starts with the pain teams already feel today — proving controls work, knowing what to fix, and reducing manual effort — then extends that into autonomous validation with Picus Swarm." primaryCta={{
      label: 'See the Platform',
      to: '/platform'
    }} secondaryCta={{
      label: 'Get a Demo',
      to: '/contact'
    }} graphic={<SignalRibbon data-id="element-356" />} data-id="element-355" />

      <div className="max-w-7xl mx-auto px-6 space-y-40 mt-20" data-id="element-357">
        {/* Section 1 */}
        <section data-id="element-358">
          <AnimatedSection data-id="element-359">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 max-w-3xl" data-id="element-360">
              The problem is not finding more issues
            </h2>
            <p className="text-picus-muted text-lg mb-12 max-w-2xl" data-id="element-361">
              Most teams already have more findings than they can work through.
              The real challenge is proving what works, knowing what matters,
              and acting before the backlog gets worse.
            </p>
          </AnimatedSection>
          <CardGrid columns={2} cards={[{
          title: 'Scanner Output',
          description: 'Thousands of alerts, disconnected dashboards, and theoretical risk.'
        }, {
          title: 'Validated Action',
          description: 'A short, prioritized list of exploitable paths with named owners.'
        }]} data-id="element-362" />
          <AnimatedSection delay={0.2} className="mt-12" data-id="element-363">
            <PlaceholderGraphic description="Split-panel before/after. Left side: scanner output, alert queues, ticket pile, disconnected dashboards. Right side: short validated action list with named owners." data-id="element-364" />
          </AnimatedSection>
        </section>

        {/* Section 2 */}
        <section data-id="element-365">
          <AnimatedSection data-id="element-366">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 max-w-3xl" data-id="element-367">
              Prove your controls work
            </h2>
            <p className="text-picus-muted text-lg mb-8 max-w-2xl" data-id="element-368">
              Breach and Attack Simulation is still the fastest path to
              confidence in the stack you already own. Picus BAS helps teams
              validate whether prevention and detection controls are doing what
              they are supposed to do.
            </p>
            <Link to="/breach-and-attack-simulation" className="inline-flex items-center text-picus-accent hover:text-white transition-colors mb-12 font-medium" data-id="element-369">
              Explore BAS <ArrowRight className="ml-2 w-4 h-4" data-id="element-370" />
            </Link>
          </AnimatedSection>
          <CardGrid columns={3} cards={[{
          title: 'Network Controls',
          description: 'Validate firewall and IPS effectiveness.'
        }, {
          title: 'Endpoint Controls',
          description: 'Test EDR prevention and telemetry.'
        }, {
          title: 'Detection Rules',
          description: 'Ensure SIEM alerts fire correctly.'
        }]} data-id="element-371" />
        </section>

        {/* Section 3 */}
        <section data-id="element-372">
          <AnimatedSection data-id="element-373">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 max-w-3xl" data-id="element-374">
              Know what actually matters
            </h2>
            <p className="text-picus-muted text-lg mb-8 max-w-2xl" data-id="element-375">
              Discovery tells you what exists. Exposure Validation tells you
              what matters. Picus uses real payloads, real environment context,
              and actual control performance to separate theoretical risk from
              evidence-backed priority.
            </p>
            <Link to="/platform/exposure-validation" className="inline-flex items-center text-picus-accent hover:text-white transition-colors mb-12 font-medium" data-id="element-376">
              Explore Exposure Validation{' '}
              <ArrowRight className="ml-2 w-4 h-4" data-id="element-377" />
            </Link>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center" data-id="element-378">
            <div className="p-8 border border-picus-border bg-picus-surface rounded-xl" data-id="element-379">
              <div className="text-3xl font-serif text-white mb-2" data-id="element-380">10,000+</div>
              <div className="text-sm text-picus-muted uppercase tracking-widest" data-id="element-381">
                Discovered Exposures
              </div>
            </div>
            <div className="p-8 border border-picus-border bg-picus-surface rounded-xl" data-id="element-382">
              <div className="text-3xl font-serif text-white mb-2" data-id="element-383">450</div>
              <div className="text-sm text-picus-muted uppercase tracking-widest" data-id="element-384">
                Validated Exposures
              </div>
            </div>
            <div className="p-8 border border-picus-accent/50 bg-picus-accent/10 rounded-xl" data-id="element-385">
              <div className="text-3xl font-serif text-picus-accent mb-2" data-id="element-386">
                12
              </div>
              <div className="text-sm text-picus-accent uppercase tracking-widest" data-id="element-387">
                Prioritized Actions
              </div>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section data-id="element-388">
          <AnimatedSection data-id="element-389">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 max-w-3xl" data-id="element-390">
              Reduce the manual gap
            </h2>
            <p className="text-picus-muted text-lg mb-8 max-w-2xl" data-id="element-391">
              Picus Swarm is the agentic operating layer of Picus Platform. It
              helps teams trigger validation on meaningful change, route the
              right context to the right owner, and reduce manual coordination
              between signal, validation, priority, and action.
            </p>
            <Link to="/platform/picus-swarm" className="inline-flex items-center text-picus-accent hover:text-white transition-colors mb-12 font-medium" data-id="element-392">
              Explore Picus Swarm <ArrowRight className="ml-2 w-4 h-4" data-id="element-393" />
            </Link>
          </AnimatedSection>
          <div className="flex flex-wrap gap-4 items-center justify-center p-8 border border-picus-border rounded-xl bg-picus-surface" data-id="element-394">
            <span className="text-picus-muted" data-id="element-395">Signal</span>
            <ArrowRight className="w-4 h-4 text-picus-border" data-id="element-396" />
            <span className="text-white" data-id="element-397">Validate</span>
            <ArrowRight className="w-4 h-4 text-picus-border" data-id="element-398" />
            <span className="text-white" data-id="element-399">Prioritize</span>
            <ArrowRight className="w-4 h-4 text-picus-border" data-id="element-400" />
            <span className="text-picus-accent" data-id="element-401">Act</span>
            <ArrowRight className="w-4 h-4 text-picus-border" data-id="element-402" />
            <span className="text-white" data-id="element-403">Revalidate</span>
          </div>
          <AnimatedSection delay={0.2} className="mt-12" data-id="element-404">
            <PlaceholderGraphic description="Workflow ribbon: signal → validate → prioritize → act → revalidate. Add small guardrail icons and human-in-loop toggles to signal governed autonomy." data-id="element-405" />
          </AnimatedSection>
        </section>

        {/* Section 5 */}
        <section data-id="element-406">
          <AnimatedSection data-id="element-407">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 max-w-3xl" data-id="element-408">
              Built to respond to what changed, not what was scheduled
            </h2>
            <p className="text-picus-muted text-lg mb-12 max-w-2xl" data-id="element-409">
              Static scans and calendar-based testing are too slow for
              environments and threats that change constantly. Picus Platform
              uses signals from your stack to trigger validation when it
              matters.
            </p>
          </AnimatedSection>
          <CardGrid columns={2} cards={[{
          title: 'Old Model',
          description: 'Calendar-driven validation. Testing happens quarterly regardless of environment changes.'
        }, {
          title: 'New Model',
          description: 'Signal-driven validation triggered by threat, asset, policy, and control changes.'
        }]} data-id="element-410" />
          <AnimatedSection delay={0.2} className="mt-12" data-id="element-411">
            <PlaceholderGraphic description="Old model: calendar-driven validation. New model: signal-driven validation triggered by threat, asset, policy, and control changes." data-id="element-412" />
          </AnimatedSection>
        </section>

        {/* Section 6 */}
        <section data-id="element-413">
          <AnimatedSection className="text-center mb-12" data-id="element-414">
            <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-415">
              Your stack already has the signals
            </h2>
            <p className="text-picus-muted text-lg max-w-2xl mx-auto mb-8" data-id="element-416">
              Picus gets context from the tools, controls, detections, policies,
              assets, and workflows already running in your environment. That
              context is what makes autonomous validation useful instead of
              generic.
            </p>
            <Link to="/integrations" className="inline-flex items-center text-picus-accent hover:text-white transition-colors font-medium" data-id="element-417">
              See Integrations <ArrowRight className="ml-2 w-4 h-4" data-id="element-418" />
            </Link>
          </AnimatedSection>
          <AnimatedSection data-id="element-419">
            <IntegrationWall data-id="element-420" />
          </AnimatedSection>
        </section>

        {/* Section 7 */}
        <section data-id="element-421">
          <AnimatedSection data-id="element-422">
            <h2 className="text-3xl md:text-4xl font-serif mb-6 max-w-3xl" data-id="element-423">
              See how the market responds to real threats
            </h2>
            <p className="text-picus-muted text-lg mb-8 max-w-2xl" data-id="element-424">
              Picus Research turns validation data into practical intelligence
              for operators, leaders, and buyers. Explore the Threat Response
              Index, Red Report, Blue Report, and Picus Labs.
            </p>
            <Link to="/research" className="inline-flex items-center text-picus-accent hover:text-white transition-colors mb-12 font-medium" data-id="element-425">
              Explore Research <ArrowRight className="ml-2 w-4 h-4" data-id="element-426" />
            </Link>
          </AnimatedSection>
          <CardGrid columns={4} cards={[{
          title: 'Threat Response Index',
          to: '/research/threat-response-index',
          tag: <ShinyText data-id="element-428">Featured</ShinyText>
        }, {
          title: 'Red Report',
          to: '/research/red-report'
        }, {
          title: 'Blue Report',
          to: '/research/blue-report'
        }, {
          title: 'Picus Labs',
          to: '/research/picus-labs'
        }]} data-id="element-427" />
        </section>

        {/* Section 8 */}
        <section className="border-t border-picus-border pt-20" data-id="element-429">
          <AnimatedSection className="text-center mb-12" data-id="element-430">
            <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-431">
              Built for teams that need evidence, not guesses
            </h2>
            <p className="text-picus-muted text-lg max-w-2xl mx-auto mb-12" data-id="element-432">
              Picus helps security teams optimize defense, improve exposure
              awareness, and scale offensive testing. It is trusted by teams
              that need proof they can defend to peers, leadership, and boards.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-16" data-id="element-433">
              {['4.9 on G2', '4.8 on Gartner Peer Insights', '#1 Innovation Index in Frost Radar 2026', '95% recommendation rate'].map((chip, i) => <span key={i} className="border border-picus-border rounded-full px-5 py-2 text-sm text-picus-muted bg-picus-surface" data-id="element-434">
                  {chip}
                </span>)}
            </div>
          </AnimatedSection>
          <div className="w-full overflow-hidden opacity-50 grayscale" data-id="element-435">
            <Marquee data-id="element-436">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="text-xl font-bold tracking-widest whitespace-nowrap px-8" data-id="element-437">
                  ENTERPRISE {i}
                </div>)}
            </Marquee>
          </div>
        </section>

        {/* Section 9 */}
        <section className="py-20 border-t border-picus-border text-center" data-id="element-438">
          <AnimatedSection data-id="element-439">
            <h2 className="text-4xl md:text-5xl font-serif mb-6" data-id="element-440">
              Start with the problem you need to solve today
            </h2>
            <p className="text-picus-muted text-lg mb-10 max-w-2xl mx-auto" data-id="element-441">
              Need BAS? Need to know what to fix? Need to reduce the manual gap
              between findings and action? Picus Platform gives you immediate
              value now and a more scalable operating model over time.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-black font-medium rounded-full hover:bg-gray-200 transition-colors" data-id="element-442">
              Get a Demo
            </Link>
          </AnimatedSection>
        </section>
      </div>
    </div>;
}